import { CssBaseline, ThemeProvider } from "@mui/material";
import { useEffect, useMemo, type PropsWithChildren } from "react";
import AuthProvider from "@/providers/AuthProvider";
import ConfirmDialogProvider from "@/providers/ConfirmDialogProvider";
import AddToWatchlistDialogMount from "@/providers/AddToWatchlistDialogProvider";
import MakeCorrectionsDialogMount from "@/providers/MakeCorrectionsDialogProvider";
import ReactQueryProvider from "@/providers/ReactQueryProvider";
import ThemeContextProvider, { useThemeContext } from "@/providers/ThemeContext";
import { useAuth } from "@/providers/useAuth";
import { watchlistsService } from "@/services/watchlistsService";
import { resolveTheme } from "@/shared/styles/theme";
import { ToastStackProvider } from "@/shared/ui/toastStack";

function MuiThemeBridge({ children }: Readonly<PropsWithChildren>) {
  const { activeThemeId, availableThemes } = useThemeContext();
  const appTheme = useMemo(
    () => resolveTheme(activeThemeId, availableThemes),
    [activeThemeId, availableThemes],
  );

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}

function AppBootstrapper({ children }: Readonly<PropsWithChildren>) {
  const { isAuthResolved, isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthResolved) {
      return;
    }

    if (!isAuthenticated) {
      // Clears the store and the loaded-once guard so a later sign-in reloads.
      watchlistsService.reset();
      return;
    }

    // `ensureLoaded` is idempotent (dedupes in-flight, loads once on success,
    // retries after a failure), so there is no need to track a bootstrap ref —
    // and a failed first load is no longer stuck empty for the session.
    void watchlistsService.ensureLoaded();
  }, [isAuthResolved, isAuthenticated]);

  return children;
}

export default function AppProviders({ children }: Readonly<PropsWithChildren>) {
  return (
    <AuthProvider>
      <AppBootstrapper>
        <ReactQueryProvider>
          <ThemeContextProvider>
            <MuiThemeBridge>
              <ToastStackProvider>
                <ConfirmDialogProvider>
                  {children}
                  <AddToWatchlistDialogMount />
                  <MakeCorrectionsDialogMount />
                </ConfirmDialogProvider>
              </ToastStackProvider>
            </MuiThemeBridge>
          </ThemeContextProvider>
        </ReactQueryProvider>
      </AppBootstrapper>
    </AuthProvider>
  );
}
